import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Review } from '@/types/api'
import { Edit, MoreVertical, Plus, Star, Trash2 } from 'lucide-react'
import React from 'react'

interface ReviewListProps {
  className?: string
  reviews: Review[]
  isLoading?: boolean
  searchTerm?: string
  currentPage?: number
  itemsPerPage?: number
  totalItems?: number
  totalPages?: number
  onSearchChange?: (_e: React.ChangeEvent<HTMLInputElement>) => void
  onPageChange?: (_page: number) => void
  onAddReview: () => void
  onEditReview: (_review: Review) => void
  onDeleteReview: (_id: string) => void
}

export const ReviewList: React.FC<ReviewListProps> = ({
  className,
  reviews,
  isLoading = false,
  searchTerm = '',
  currentPage = 1,
  itemsPerPage = 10,
  totalItems = 0,
  totalPages = 0,
  onSearchChange,
  onPageChange,
  onAddReview,
  onEditReview,
  onDeleteReview,
}) => {
  const formatDate = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('id-ID')
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Product Reviews</CardTitle>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-sm">
            <Input
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={onSearchChange}
              className="w-full"
            />
          </div>
          <Button onClick={onAddReview}>
            <Plus className="h-4 w-4 mr-2" />
            Add Review
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="mb-4 text-sm text-muted-foreground">Loading reviews...</div>}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Review</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No reviews found
                  </TableCell>
                </TableRow>
              ) : (
                reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell className="font-medium">
                      {review.product?.name || 'Unknown Product'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{review.user?.full_name || 'Anonymous'}</span>
                        <span className="text-xs text-muted-foreground">{review.user?.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-yellow-500">
                        <span className="text-foreground text-sm mr-1 font-semibold">
                          {review.rating}
                        </span>
                        <Star className="w-4 h-4 fill-current" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="line-clamp-2 max-w-xs text-muted-foreground">{review.review}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {review.is_verified && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 w-fit">
                            Verified
                          </Badge>
                        )}
                        {review.is_featured && (
                          <Badge
                            variant="secondary"
                            className="bg-purple-100 text-purple-800 w-fit"
                          >
                            Featured
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(review.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditReview(review)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onDeleteReview(review.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange || (() => {})}
              onItemsPerPageChange={() => {}} // Not implemented yet in ReviewsPage
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
